import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  loginStart,
  loginSuccess,
  loginFailure,
  clearError
} from '../../store/authSlice'
import apiService from '../../services/api'
import {
  User,
  Lock,
  Eye,
  EyeOff,
  ArrowRight
} from 'lucide-react'
import Logo from '../common/Logo'
import './Login.css'

const Login = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { loading, error, isAuthenticated } = useSelector(state => state.auth)

  const [formData, setFormData] = useState({
    username: '',
    password: ''
  })

  const [validationErrors, setValidationErrors] = useState({})
  const [showPassword, setShowPassword] = useState(false)

  // 如果已经登录，重定向到仪表盘
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard')
    }
  }, [isAuthenticated, navigate])

  // 清除错误当组件卸载时
  useEffect(() => {
    return () => {
      dispatch(clearError())
    }
  }, [dispatch])

  const validateForm = () => {
    const errors = {}

    if (!formData.username.trim()) {
      errors.username = '用户名不能为空'
    } else if (formData.username.length < 3) {
      errors.username = '用户名至少需要3个字符'
    }

    if (!formData.password) {
      errors.password = '密码不能为空'
    } else if (formData.password.length < 6) {
      errors.password = '密码至少需要6个字符'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))

    // 清除该字段的验证错误
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    dispatch(loginStart())

    try {
      const response = await apiService.auth.login(formData)
      console.log('Login response:', response.data)

      const responseData = response.data

      if (!responseData.success) {
        throw new Error(responseData.message || '登录失败')
      }

      const { data } = responseData

      if (!data || !data.token || !data.user) {
        throw new Error('响应数据格式错误')
      }

      localStorage.setItem('authToken', data.token)
      localStorage.setItem('user', JSON.stringify(data.user))

      dispatch(loginSuccess({
        user: data.user,
        token: data.token
      }))

      navigate('/dashboard')
    } catch (error) {
      console.error('Login error:', error)
      const errorMessage = error.response?.data?.error?.message ||
        error.response?.data?.message ||
        error.message ||
        '登录失败，请重试'
      dispatch(loginFailure(errorMessage))
    }
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  return (
    <div className="login-page">
      <div className="login-container">
        {/* 背景装饰 */}
        <div className="login-background">
          {/* 网格图案 */}
          <div className="grid-pattern"></div>

          {/* 数据流动画 */}
          <div className="data-stream stream-1"></div>
          <div className="data-stream stream-2"></div>
          <div className="data-stream stream-3"></div>
          <div className="data-stream stream-4"></div>

          {/* 几何线条 */}
          <div className="geometric-lines">
            <div className="geo-line geo-line-1" style={{ '--rotation': '-15deg' }}></div>
            <div className="geo-line geo-line-2" style={{ '--rotation': '20deg' }}></div>
            <div className="geo-line geo-line-3" style={{ '--rotation': '-10deg' }}></div>
          </div>

          {/* 光晕效果 */}
          <div className="glow-orb orb-1"></div>
          <div className="glow-orb orb-2"></div>
        </div>

        <div className="login-wrapper">
          {/* Logo 和标题 */}
          <div className="login-header">
            <div className="logo-wrapper">
              <div className="logo-icon">
                <Logo width={36} height={36} className="logo-svg" />
              </div>
            </div>
            <h1 className="login-title">交易量管理系统</h1>
            <p className="login-subtitle">登录以继续访问</p>
          </div>

          <form className="login-form" onSubmit={handleSubmit} noValidate>
            {error && (
              <div className="alert-error">
                <div className="alert-icon">
                  <Logo width={20} height={20} />
                </div>
                <span>{error}</span>
              </div>
            )}

            {/* 用户名输入 */}
            <div className="form-group">
              <label htmlFor="username" className="form-label">
                用户名
              </label>
              <div className="input-wrapper">
                <User size={20} className="input-icon" />
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  className={`form-input ${validationErrors.username ? 'error' : ''}`}
                  placeholder="请输入用户名"
                  autoComplete="username"
                  disabled={loading}
                />
              </div>
              {validationErrors.username && (
                <span className="error-text">
                  {validationErrors.username}
                </span>
              )}
            </div>

            {/* 密码输入 */}
            <div className="form-group">
              <label htmlFor="password" className="form-label">
                密码
              </label>
              <div className="input-wrapper">
                <Lock size={20} className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`form-input ${validationErrors.password ? 'error' : ''}`}
                  placeholder="请输入密码"
                  autoComplete="current-password"
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={togglePasswordVisibility}
                  disabled={loading}
                  aria-label={showPassword ? '隐藏密码' : '显示密码'}
                >
                  {showPassword ? (
                    <EyeOff size={18} />
                  ) : (
                    <Eye size={18} />
                  )}
                </button>
              </div>
              {validationErrors.password && (
                <span className="error-text">
                  {validationErrors.password}
                </span>
              )}
            </div>

            {/* 登录按钮 */}
            <button
              type="submit"
              className="login-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="loading-spinner"></span>
                  <span>登录中...</span>
                </>
              ) : (
                <>
                  <span>登录</span>
                  <ArrowRight size={20} className="btn-icon" />
                </>
              )}
            </button>
          </form>

          {/* 页脚 */}
          <div className="login-footer">
            <p className="footer-text">
              © 2026 交易量管理系统. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
